// Safe, resilient getUserMedia wrapper that always calls the original browser API
// and adds basic retries with relaxed constraints handling.
// Usage:
//   const { stream } = await getUserMediaSafe({ audio: true, video: true });

function mapError(e) {
  const name = e?.name || '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return { kind: 'not-allowed' };
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return { kind: 'not-found' };
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return { kind: 'overconstrained' };
    case 'NotReadableError':
    case 'TrackStartError':
      return { kind: 'not-readable' };
    case 'AbortError':
      return { kind: 'abort' };
    case 'TypeError':
      return { kind: 'type' };
    default:
      return { kind: 'unknown' };
  }
}

function relaxConstraints(c) {
  try {
    const copy = JSON.parse(JSON.stringify(c));
    if (copy?.video && typeof copy.video === 'object') {
      const v = copy.video;
      // Drop tight dimension constraints to improve odds
      delete v.width;
      delete v.height;
      if (typeof v.frameRate === 'object') {
        v.frameRate.ideal = Math.min(v.frameRate.ideal ?? 30, 30);
        v.frameRate.max = Math.min(v.frameRate.max ?? 30, 30);
      } else {
        v.frameRate = { ideal: 24, max: 30 };
      }
    }
    return copy;
  } catch {
    return c;
  }
}

function getOriginalGUM() {
  const md = navigator.mediaDevices;
  const gum =
      md?.__originalGetUserMedia?.bind(md) ||
      md?.getUserMedia?.bind(md);
  if (!gum) {
    const err = new Error('getUserMedia is not supported in this context');
    err.name = 'TypeError';
    throw err;
  }
  return gum;
}

/**
 * getUserMediaSafe
 * @param {object} opts
 *  - audio: boolean|MediaTrackConstraints
 *  - video: boolean|MediaTrackConstraints
 *  - retryOnNotReadable: boolean (default true)
 * @returns {Promise<{stream: MediaStream, appliedConstraints: MediaStreamConstraints}>}
 */
export async function getUserMediaSafe(opts = { audio: true, video: true, retryOnNotReadable: true }) {
  const audio =
      typeof opts.audio === 'object'
          ? opts.audio
          : opts.audio === false
              ? false
              : { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

  const video =
      typeof opts.video === 'object'
          ? opts.video
          : opts.video
              ? {
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 30, max: 30 },
              }
              : false;

  const constraints = { audio, video };
  const gum = getOriginalGUM();

  try {
    const stream = await gum(constraints);
    // Some buggy devices may return ended tracks immediately; treat as NotReadable and retry logic will handle.
    if (stream.getTracks().some(t => t.readyState === 'ended')) {
      try { stream.getTracks().forEach(t => t.stop()); } catch {}
      const err = new Error('Tracks ended immediately');
      err.name = 'NotReadableError';
      throw err;
    }
    return { stream, appliedConstraints: constraints };
  } catch (e) {
    const kind = mapError(e).kind;

    // If constraints are too tight, relax some video constraints and retry once
    if (kind === 'overconstrained') {
      const relaxed = relaxConstraints(constraints);
      const stream = await gum(relaxed);
      return { stream, appliedConstraints: relaxed };
    }

    // Transient device state; retry once
    if (kind === 'not-readable' && (opts.retryOnNotReadable ?? true)) {
      const retry = await gum(constraints);
      return { stream: retry, appliedConstraints: constraints };
    }

    throw e;
  }
}